import { Link } from "react-router-dom";
import { Check, Users, Luggage, ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { FLEET, COMPANY, BOOKING_URL } from "@/data/site";

const features = ["Air-conditioned & valeted", "Card & contactless payment", "Child seats on request", "DBS-checked drivers"];

export default function Fleet() {
  return (
    <Layout>
      <Seo title="Our Fleet" description="Our modern 2026 fleet — Saloon, MPV Medium, MPV Large, Executive, Wheelchair-accessible and Minibus — driven by professional, licensed chauffeurs." path="/fleet" />
      <section className="pt-16 pb-14 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-brand font-semibold uppercase tracking-[0.2em] text-xs mb-3">Our fleet</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">Luxury vehicles for every journey</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Every vehicle in the Clocktower Cars Gatwick fleet is a modern, immaculately maintained 2026 model, driven by a licensed professional chauffeur. Click any vehicle to explore specs, features and passenger capacity.
          </p>
        </div>
      </section>

      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FLEET.map((f) => (
              <Link
                key={f.slug}
                to={`/fleet/${f.slug}`}
                data-testid={`fleet-grid-${f.slug}`}
                className="group flex flex-col bg-white border border-[#EAEBEA] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] transition-all duration-300"
              >
                <div className="h-56 overflow-hidden">
                  <img src={f.image} alt={f.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-[11px] uppercase tracking-widest text-brand font-bold">{f.tier}</span>
                  <h3 className="font-heading text-xl font-bold text-ink mt-1 group-hover:text-brand transition-colors">{f.name}</h3>
                  <p className="text-sm text-ink/60 leading-relaxed mt-2 mb-4 line-clamp-3 flex-1">{f.desc}</p>
                  <div className="flex items-center justify-between gap-4 mt-auto pt-4 border-t border-[#F0F0F0]">
                    <div className="flex gap-4 text-sm text-ink/60">
                      <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-brand" /> {f.seats}</span>
                      <span className="flex items-center gap-1.5"><Luggage className="h-4 w-4 text-brand" /> {f.luggage}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-brand font-semibold text-sm group-hover:gap-2 transition-all">
                      View <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <ul className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {features.map((ft) => (
              <li key={ft} className="flex items-center gap-2 text-sm text-slate-700 bg-white rounded-xl border border-slate-200 px-4 py-3">
                <Check className="h-4 w-4 text-brand shrink-0" /> {ft}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 bg-white border-t border-slate-200 text-center">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Not sure which to pick?</h2>
          <p className="text-slate-600 mb-6">Call our team and we&apos;ll recommend the perfect vehicle for your journey.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={BOOKING_URL} data-testid="fleet-book-online" className="bg-brand hover:bg-brand-light text-white px-8 py-4 rounded-full font-bold transition-colors">Book online</a>
            <a href={`tel:${COMPANY.phoneRaw}`} className="border-2 border-ink/10 hover:border-ink text-ink px-8 py-4 rounded-full font-bold transition-colors inline-block">Call {COMPANY.phone}</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
